/*
 * jrs-bundle-size-grid.js
 *
 * Client-side renderer for bundle colour-block size grids.
 *
 * Why this exists: Liquid's `product.variants` AND `/products/{handle}.js`
 * (and `.json`) all return at most 250 variants per product. Bundle products
 * that exceed that — e.g. JH001 Just Hoods College Hoodie has 100 colours ×
 * up to 9 sizes ≈ 730 variants — silently lose sizes from the grid rendered
 * by snippets/jrs-sizes-inner--flat.liquid. With the JS endpoint capped, the
 * first ~62 colours got XS/S/M/L only and 14 colours dropped entirely.
 *
 * Fix: fetch via the Storefront API GraphQL endpoint (/api/{ver}/graphql.json)
 * with cursor-based variant pagination. That endpoint accepts unauthenticated
 * public reads on this shop and returns ALL variants when paged.
 *
 * Inventory: `quantityAvailable` is gated behind the
 * `unauthenticated_read_product_inventory` Storefront scope. When the Headless
 * sales channel token is set in `_STOREFRONT_TOKEN` below, we send it on the
 * GraphQL request and ask for `quantityAvailable`, getting real counts for ALL
 * variants — past the 250-variant Liquid cap. When the token is empty (default
 * checked-in state until the merchant provisions a Headless storefront), the
 * helper falls back to anonymous Storefront calls + the server-rendered Liquid
 * map for inventory; variants past 250 then render as "In stock" without a count.
 *
 * Public API:
 *   JRS.BundleSizeGrid.render(rootEl)        // render every unrendered mount under rootEl
 *   JRS.BundleSizeGrid.renderOne(gridEl)     // render a specific mount element
 *   JRS.BundleSizeGrid.fetchProduct(handle)  // promise of cached product JSON
 *
 * Output structure mirrors what the Liquid snippet produced exactly so existing
 * CSS, qty-stepper bindings (bindEvents in betterconversions-bundle.js) and
 * cart payload builders keep working without changes.
 */
window.JRS = window.JRS || {};

(function(JRS) {
  'use strict';

  var _cache = {};
  var _inflight = {};
  var _invMapByHandle = null; // { handle: { variantId: inventoryQty | null } }

  // Reads <script type="application/json" data-jrs-inventory-map data-product-handle="...">
  // blocks emitted by sections/bundle{,-new}.liquid. The map only covers a product's first
  // 250 variants (Liquid cap) plus any combination_products — past that, GraphQL gives us
  // the variant but Shopify withholds inventory without an auth token, so values stay null.
  function _ensureInventoryMaps() {
    if (_invMapByHandle) return _invMapByHandle;
    _invMapByHandle = {};
    var nodes;
    try { nodes = document.querySelectorAll('script[data-jrs-inventory-map][data-product-handle]'); }
    catch (e) { return _invMapByHandle; }
    for (var i = 0; i < nodes.length; i++) {
      var handle = nodes[i].getAttribute('data-product-handle');
      if (!handle) continue;
      var raw = (nodes[i].textContent || '').trim();
      if (!raw) continue;
      try { _invMapByHandle[handle] = JSON.parse(raw) || {}; }
      catch (e) {
        try { console.warn('[BundleSizeGrid] failed to parse inventory map for ' + handle, e); } catch (_) {}
        _invMapByHandle[handle] = {};
      }
    }
    return _invMapByHandle;
  }

  function _inventoryFor(handle, variantId) {
    var maps = _ensureInventoryMaps();
    var m = maps[handle];
    if (!m) return null;
    var v = m[String(variantId)];
    if (v == null) return null;
    var n = Number(v);
    return isFinite(n) ? n : null;
  }

  // Any of 2024-04 .. 2025-10 .. unstable currently return 200 on this shop.
  // Pinning to a stable named version protects against schema drift on `unstable`.
  var _GQL_API_VERSION = '2025-01';
  var _GQL_PAGE_SIZE = 250; // max page size on Storefront API ProductVariantConnection.

  // Storefront API public access token from the Shopify Headless sales channel.
  // Required scopes on the Headless storefront: `unauthenticated_read_product_listings`
  // (always) + `unauthenticated_read_product_inventory` (to expose `quantityAvailable`
  // for variants past Liquid's 250 cap). Public by design — safe to embed in client
  // code. When empty, the helper falls back to anonymous Storefront calls (works for
  // listings + variant pagination, but inventory_quantity stays null past variant
  // 250 and the UI shows "In stock" without a count). Rotate by replacing this value
  // and re-deploying.
  var _STOREFRONT_TOKEN = '';

  function _gqlEndpoint() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    if (root.charAt(root.length - 1) !== '/') root += '/';
    return root + 'api/' + _GQL_API_VERSION + '/graphql.json';
  }

  // gid://shopify/ProductVariant/56729327075701  ->  56729327075701
  function _extractNumericId(gid) {
    if (gid == null) return null;
    var s = String(gid);
    var m = s.match(/(\d+)$/);
    if (m) return Number(m[1]);
    var n = Number(s);
    return isFinite(n) ? n : null;
  }

  // Storefront API gives price as a decimal-string in major units ("8.33"); the
  // rest of the codebase (and `_fmtMoney`) expect pence. Convert at the boundary.
  function _toPence(amountStr) {
    if (amountStr == null || amountStr === '') return 0;
    var n = Number(amountStr);
    if (!isFinite(n)) return 0;
    return Math.round(n * 100);
  }

  function _normalizeGqlVariant(node, productId) {
    if (!node) return null;
    var opts = Array.isArray(node.selectedOptions) ? node.selectedOptions : [];
    var v = {
      id: _extractNumericId(node.id),
      product_id: productId,
      title: node.title,
      sku: node.sku || '',
      available: !!node.availableForSale,
      // `quantityAvailable` requires the unauthenticated_read_product_inventory
      // Storefront scope (granted via _STOREFRONT_TOKEN). Falls back to null when
      // the scope is missing or the field is absent — `_inventoryFor()` then merges
      // any server-rendered Liquid map value as a secondary source.
      inventory_quantity: typeof node.quantityAvailable === 'number' ? node.quantityAvailable : null,
      inventory_management: 'shopify',
      price: node.price ? _toPence(node.price.amount) : 0,
      compare_at_price: node.compareAtPrice ? _toPence(node.compareAtPrice.amount) : null,
      featured_image: node.image && node.image.url ? { src: node.image.url } : null,
      options: []
    };
    for (var i = 0; i < opts.length; i++) {
      v['option' + (i + 1)] = opts[i].value;
      v.options.push(opts[i].value);
    }
    return v;
  }

  function _gqlFetchPage(handle, cursor) {
    // Inline literal — handle is from theme HTML data attr we control. Still
    // escape quotes/backslashes defensively to keep this safe against
    // pathological merchant data.
    var safeHandle = String(handle).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    var afterClause = cursor ? ', after: "' + String(cursor).replace(/\\/g, '\\\\').replace(/"/g, '\\"') + '"' : '';
    // `quantityAvailable` is only requested when the Headless token is present —
    // anonymous calls don't have the inventory scope and would error on this field.
    var quantityField = _STOREFRONT_TOKEN ? ' quantityAvailable' : '';
    var query = '{ product(handle: "' + safeHandle + '") { id title options { name values } variants(first: ' + _GQL_PAGE_SIZE + afterClause + ') { edges { node { id title sku availableForSale' + quantityField + ' price { amount } compareAtPrice { amount } selectedOptions { name value } image { url } } } pageInfo { hasNextPage endCursor } } } }';
    var headers = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
    if (_STOREFRONT_TOKEN) headers['X-Shopify-Storefront-Access-Token'] = _STOREFRONT_TOKEN;
    return fetch(_gqlEndpoint(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: headers,
      body: JSON.stringify({ query: query })
    }).then(function(r) {
      if (!r.ok) throw new Error('HTTP ' + r.status + ' from Storefront GraphQL');
      return r.json();
    }).then(function(json) {
      // Tolerate per-field errors (e.g. denied scope on optional fields) but log.
      if (json.errors && json.errors.length) {
        try { console.warn('[BundleSizeGrid] GraphQL non-fatal errors:', json.errors); } catch (e) {}
      }
      return json.data && json.data.product;
    });
  }

  function fetchProduct(handle) {
    if (!handle) return Promise.reject(new Error('jrs-bundle-size-grid: missing product handle'));
    if (_cache[handle]) return Promise.resolve(_cache[handle]);
    if (_inflight[handle]) return _inflight[handle];

    var allEdges = [];
    var header = null;

    function loop(cursor) {
      return _gqlFetchPage(handle, cursor).then(function(prod) {
        if (!prod) throw new Error('Product not found via Storefront GraphQL: ' + handle);
        if (!header) {
          header = {
            id: _extractNumericId(prod.id),
            title: prod.title,
            options: (prod.options || []).map(function(o) { return o && o.name; })
          };
        }
        var edges = (prod.variants && prod.variants.edges) || [];
        for (var i = 0; i < edges.length; i++) allEdges.push(edges[i]);
        var info = (prod.variants && prod.variants.pageInfo) || {};
        if (info.hasNextPage && info.endCursor) return loop(info.endCursor);
      });
    }

    var p = loop(null).then(function() {
      var pid = header && header.id;
      var variants = [];
      for (var i = 0; i < allEdges.length; i++) {
        var nv = _normalizeGqlVariant(allEdges[i].node, pid);
        if (!nv) continue;
        // GraphQL `quantityAvailable` is the primary source. Fall back to the
        // server-rendered Liquid map only when GraphQL didn't return a count
        // (e.g. token rotated away, scope revoked, or pre-token cached calls).
        if (nv.inventory_quantity == null) {
          var inv = _inventoryFor(handle, nv.id);
          if (inv != null) nv.inventory_quantity = inv;
        }
        variants.push(nv);
      }
      var product = {
        id: pid,
        handle: handle,
        title: header && header.title,
        options: (header && header.options) || [],
        variants: variants
      };
      _cache[handle] = product;
      delete _inflight[handle];
      return product;
    }).catch(function(err) {
      delete _inflight[handle];
      throw err;
    });
    _inflight[handle] = p;
    return p;
  }

  // /products/{handle}.js may return options as either ["Color","Size"] or
  // [{name:"Color",position:1,values:[...]}, ...]. Normalise to plain strings.
  function _normalizeOptionNames(options) {
    if (!Array.isArray(options)) return [];
    var out = [];
    for (var i = 0; i < options.length; i++) {
      var o = options[i];
      if (typeof o === 'string') out.push(o);
      else if (o && typeof o === 'object' && o.name) out.push(o.name);
      else out.push('');
    }
    return out;
  }

  function _findSizeOptionIndex(optionNames) {
    if (!Array.isArray(optionNames)) return -1;
    for (var i = 0; i < optionNames.length; i++) {
      var name = String(optionNames[i] || '').toLowerCase();
      if (name === 'size' || name.indexOf('size') !== -1) return i;
    }
    return -1;
  }

  // variant.option1/2/3 OR variant.options[i] — Shopify responses include both
  // historically; some legacy endpoints only set one. Read whichever exists.
  function _variantOption(variant, idx /* 0-based */) {
    if (!variant) return null;
    var keyed = variant['option' + (idx + 1)];
    if (keyed != null && keyed !== '') return keyed;
    if (Array.isArray(variant.options) && variant.options[idx] != null) return variant.options[idx];
    return null;
  }

  function _escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function _handleize(s) {
    return String(s == null ? '' : s)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  // £X.XX (matches existing rendered output). variant.price is in pence (smallest currency unit).
  // Currency symbol is hardcoded to match the storefront — Liquid's `money` filter would have
  // produced the same output here. If the shop ever multi-currency, swap to Shopify.formatMoney.
  function _fmtMoney(cents) {
    var n = Number(cents) || 0;
    return '£' + (n / 100).toFixed(2);
  }

  // Mirrors snippets/mrk-price--v2.liquid in `bundle_row: true` mode.
  function _renderBundleRowPrice(variant, available) {
    var price = Number(variant.price) || 0;
    var compare = Number(variant.compare_at_price) || 0;
    // in-VAT = price × 1.2 ; ex-VAT display = price (pence)
    var inVat = _fmtMoney(price * 1.2);
    var exVat = _fmtMoney(price);
    var cls = 'price';
    if (!available) cls += ' price--sold-out';
    if (compare > price) cls += ' price--on-sale';
    cls += ' price--bundle-row';
    return (
      '<div class="' + cls + '">' +
        '<div class="flex items-center text-sm font-medium" data-price-wrapper>' +
          '<span class="price-item price-item--regular text-[15px] font-bold">' +
            '<span class="price in-vat">' + inVat + '</span>' +
            '<span class="price ex-vat hidden">' + exVat + '</span>' +
          '</span>' +
        '</div>' +
      '</div>'
    );
  }

  function _renderCard(variant, sizeValue, idx, colorName, productTitle) {
    var available = !!variant.available;
    // Storefront GraphQL doesn't expose inventory_quantity to unauthenticated
    // callers (file header explains). When null/undefined, show plain availability.
    var rawInv = variant.inventory_quantity;
    var hasInvCount = (rawInv != null && isFinite(Number(rawInv)));
    var inv = hasInvCount ? Number(rawInv) : 0;
    var lowStock = hasInvCount && inv > 0 && inv <= 20;
    var maxQty = hasInvCount && inv > 0 ? inv : 100;
    var cls = 'sc';
    if (lowStock) cls += ' sc--low-stock';
    if (!available) cls += ' pointer-events-none opacity-50';

    var stockText;
    if (!available) stockText = 'Sold out';
    else if (hasInvCount) stockText = inv + ' in stock';
    else stockText = 'In stock';
    var compareValue = (variant.compare_at_price != null ? variant.compare_at_price : variant.price);

    return (
      '<div class="' + cls + '" data-size="' + _escapeHtml(_handleize(sizeValue)) + '">' +
        (lowStock ? '<span class="sc__low-badge">Low</span>' : '') +
        '<span class="sc__label">' + _escapeHtml(sizeValue) + '</span>' +
        '<span class="sc__price">' + _renderBundleRowPrice(variant, available) + '</span>' +
        '<span class="sc__stock" aria-label="Stock for ' + _escapeHtml(sizeValue) + '">' + _escapeHtml(stockText) + '</span>' +
        '<div class="sc__stepper stock">' +
          '<span data-action="decrement" class="sc__btn sc__btn--minus" aria-label="Decrease quantity">−</span>' +
          '<input type="number" class="mrk-qty-item sc__val"' +
            ' name="items[' + idx + '][quantity]"' +
            ' value="0" min="0" max="' + maxQty + '"' +
            ' color="' + _escapeHtml(colorName) + '"' +
            ' size="' + _escapeHtml(sizeValue) + '"' +
            ' productname="' + _escapeHtml(productTitle) + '"' +
            ' data-variant-id="' + _escapeHtml(variant.id) + '"' +
            ' data-product-id="' + _escapeHtml(variant.product_id || '') + '"' +
            ' data-price="' + _escapeHtml(variant.price) + '"' +
            ' data-compare="' + _escapeHtml(compareValue) + '"' +
            ' price="' + _escapeHtml(variant.price) + '"' +
            ' sku="' + _escapeHtml(variant.sku || '') + '"' +
            // Editable: the input listener in betterconversions-bundle.js bindEvents() clamps
            // typed values in real time to min(maxAttr, categoryLimit − sumOfOtherInputs).
            ' inputmode="numeric" pattern="[0-9]*" aria-label="Quantity ' + _escapeHtml(sizeValue) + '">' +
          '<span data-action="increment" class="sc__btn sc__btn--plus" aria-label="Increase quantity">+</span>' +
        '</div>' +
        '<input type="hidden" name="items[' + idx + '][id]" value="' + _escapeHtml(variant.id) + '">' +
      '</div>'
    );
  }

  function renderOne(gridEl) {
    if (!gridEl) return Promise.resolve();
    var handle = gridEl.getAttribute('data-product-handle');
    var colorName = gridEl.getAttribute('data-color-name');
    var productTitle = gridEl.getAttribute('data-product-title') || '';
    if (!handle || !colorName) {
      gridEl.innerHTML = '<div class="flat-grid__empty" role="status">Missing product handle or colour name.</div>';
      gridEl.removeAttribute('aria-busy');
      try { console.warn('[BundleSizeGrid] mount missing data-product-handle/data-color-name', { handle: handle, colorName: colorName, gridEl: gridEl }); } catch (e) {}
      return Promise.resolve();
    }
    return fetchProduct(handle).then(function(product) {
      var optionNames = _normalizeOptionNames(product && product.options);
      var sizeIdx = _findSizeOptionIndex(optionNames);
      var variants = (product && product.variants) || [];
      var diagnostics = {
        handle: handle,
        colorName: colorName,
        optionNames: optionNames,
        sizeOptionIndex: sizeIdx,
        variantCount: variants.length,
        sampleVariant: variants[0] || null
      };

      if (sizeIdx < 0) {
        gridEl.innerHTML = '<div class="flat-grid__empty" role="status">No size option found for this product.</div>';
        gridEl.removeAttribute('aria-busy');
        try { console.warn('[BundleSizeGrid] no size option in product.options', diagnostics); } catch (e) {}
        return;
      }

      var colorLower = String(colorName).toLowerCase();
      var matching = [];
      for (var v = 0; v < variants.length; v++) {
        var variant = variants[v];
        for (var i = 0; i < 3; i++) {
          if (i === sizeIdx) continue;
          var ov = _variantOption(variant, i);
          if (ov != null && String(ov).toLowerCase() === colorLower) {
            matching.push(variant);
            break;
          }
        }
      }

      // Preserve Shopify's variant order so sizes appear in the merchant-set sequence.
      var seen = {};
      var ordered = [];
      for (var k = 0; k < matching.length; k++) {
        var sz = _variantOption(matching[k], sizeIdx);
        if (sz != null && !seen[sz]) {
          seen[sz] = true;
          ordered.push({ size: sz, variant: matching[k] });
        }
      }

      diagnostics.matchingVariants = matching.length;
      diagnostics.uniqueSizes = ordered.length;

      if (ordered.length === 0) {
        gridEl.innerHTML = '<div class="flat-grid__empty" role="status">No sizes available for ' + _escapeHtml(colorName) + '.</div>';
        gridEl.removeAttribute('aria-busy');
        try {
          console.warn('[BundleSizeGrid] zero size matches', diagnostics);
          if (variants.length > 0 && variants[0]) {
            console.warn('[BundleSizeGrid] first variant:', variants[0]);
          }
        } catch (e) {}
        return;
      }

      var parts = [];
      for (var c = 0; c < ordered.length; c++) {
        parts.push(_renderCard(ordered[c].variant, ordered[c].size, c, colorName, productTitle));
      }
      gridEl.innerHTML = parts.join('');
      gridEl.removeAttribute('aria-busy');
      try { console.log('[BundleSizeGrid] rendered ' + ordered.length + ' sizes', diagnostics); } catch (e) {}
    }).catch(function(err) {
      try { console.warn('[BundleSizeGrid] render failed for handle=' + handle + ' colour=' + colorName, err); } catch (e) {}
      try {
        if ((window.JRS && JRS.ErrorTelemetry && typeof JRS.ErrorTelemetry.report === 'function')) {
          JRS.ErrorTelemetry.report('bundle_grid', 'size_grid_load_failed', {
            message: 'Could not load sizes. Please refresh and try again.',
            detail: String((err && err.message) || err || ''),
            product_handle: String(handle || ''),
            colour: String(colorName || '')
          });
        }
      } catch (e2) {}
      gridEl.innerHTML = '<div class="flat-grid__error" role="alert">Could not load sizes. Please refresh and try again.</div>';
      gridEl.removeAttribute('aria-busy');
    });
  }

  function renderAll(rootEl) {
    var root = rootEl || document;
    var grids;
    try {
      grids = root.querySelectorAll('[data-jrs-flat-grid]:not([data-jrs-flat-grid-rendered])');
    } catch (e) {
      return Promise.resolve();
    }
    if (!grids || !grids.length) return Promise.resolve();
    var promises = [];
    for (var i = 0; i < grids.length; i++) {
      grids[i].setAttribute('data-jrs-flat-grid-rendered', '1');
      promises.push(renderOne(grids[i]));
    }
    return Promise.all(promises);
  }

  JRS.BundleSizeGrid = {
    render: renderAll,
    renderOne: renderOne,
    fetchProduct: fetchProduct,
    _cache: _cache
  };

  // Auto-render any grids present at DOM ready (initial page load, before the colour-change AJAX flow).
  function _autoInit() {
    if (!document.querySelector || !document.querySelector('[data-jrs-flat-grid]')) return;
    renderAll(document);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', _autoInit);
  } else {
    _autoInit();
  }
})(window.JRS);
